import React, { useEffect, useState, useRef } from "react";
import { Marker, Source, Layer, Popup, MapRef } from "react-map-gl";
import { DateTime } from "luxon";

import CustomOverlay from "../../map/customOverlay";
import { OrderStatusEnum } from "../constants/orderStatusEnum";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLocationDot,
  faPersonWalking,
} from "@fortawesome/free-solid-svg-icons";
import io from "socket.io-client";

import "./styles.css";
import { useDispatch, useSelector } from "react-redux";
import {
  setAvailableRunners,
  setselectedRunners,
  setselectedRunnersView,
} from "../../store/showRunners";
import { RootState } from "../../store";
import { Close } from "@mui/icons-material";
import CircleIcon from "@mui/icons-material/Circle";
import axios from "axios";
import OrderRoute from "./orderBox/orderRoutes";

// TypeScript interfaces
interface UserAddress {
  id: number;
  name: string;
  phone: string;
  email: string;
  facebook_id?: string | null;
  google_id?: string | null;
  apple_id?: string | null;
  spesa_totale?: number | null;
  rimborsato?: number | null;
  n_ordini: number;
  first_order?: string | null;
  last_order?: string | null;
  favourite_district: string;
  favourite_latitude: number;
  favourite_longitude: number;
  favourite_civic_number?: any;
}

interface UserAddressesHookReturn {
  userAddresses: UserAddress[];
  loading: boolean;
  selectedDistricts: string[];
  setSelectedDistricts: React.Dispatch<React.SetStateAction<string[]>>;
  refresh: () => Promise<void>;
}

interface UserAddressControlsProps {
  userAddresses: UserAddress[];
  userAddressesLoading: boolean;
  selectedUserDistricts: string[];
  userDistricts: string[];
  toggleUserDistrict?: (district: any) => void;
  clearUserDistrictFilters?: () => void;
  refreshUserAddresses?: () => Promise<void>;
  showNoOrdersCallback?: (showZeroOrderAddresses: boolean) => void;
}

interface UserAddressesLayerProps {
  map?: MapRef | null;
  userAddresses: UserAddress[];
  selectedDistricts: string[];
  onAddressClick?: any;
  showZeroOrderAdress?: boolean;
}

interface GetOrdersParams {
  date_from: string;
  date_to: string;
  accessToken: string;
}

interface Runner {
  runner_id: number;
  longitude: number;
  latitude: number;
  positions: string;
  users: {
    id: number;
    fullname: string;
    runner_days?: Array<{
      saturation_days?: {
        range_hour?: {
          start_range?: string;
          end_range?: string;
        };
      };
    }>;
    runner_orders?: any[];
  };
}

interface Order {
  id: number;
  store_id: number;
  status: {
    name: string;
    styles: {
      background: string;
    };
  };
  store: {
    latitude: number;
    longitude: number;
  };
  food_orders?: {
    latitude: number;
    longitude: number;
  };
  smart_logistics_orders?: {
    latitude: number;
    longitude: number;
  };
  runner_orders: Array<{
    user_id: number;
    created_at: string;
  }>;
  pickupTime: string;
  runner_walking_time?: number;
  distance?: number;
  help_requests: Array<{
    motivation: string;
    method: string;
    note: string;
    phone_number: string;
    served: boolean;
  }>;
}

interface RunnerColorMatch {
  runnerId: number | null;
  color: string;
  name: string;
  count: number;
}

interface MapAdminProps {
  map?: MapRef | null;
  access_token: string;
  refresh: boolean;
  stores: any[];
  markers: any[];
  popupInfo: any;
  categories: string[];
  pathPoints: any;
  route: any;
  steps: any;
  allOrders: Order[];
  showDirectioAdmin?: any;
  showpath: (coordinates: any, showRoute?: boolean) => void;
  removePopUp: () => void;
  onGeolocate: (event: any) => void;
  onrecieveRunnerDetail: (data: any) => void;
  onReceiveData: (data: any) => void;
}

interface DistrictColors {
  [key: string]: string;
}

// Custom hook for fetching user addresses
const useUserAddresses = (accessToken: string): UserAddressesHookReturn => {
  const [userAddresses, setUserAddresses] = useState<UserAddress[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);

  const fetchUserAddresses = async (): Promise<void> => {
    if (!accessToken) return;

    try {
      setLoading(true);
      const response = await fetch(
        ` ${process.env.REACT_APP_API}/user/consumers?_end=100&_start=0`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user addresses");
      }

      const data = await response.json();

      // Filter users that have coordinates
      const usersWithCoords: UserAddress[] = data.data.filter(
        (user: UserAddress) =>
          user.favourite_latitude &&
          user.favourite_longitude &&
          user.favourite_district
      );

      setUserAddresses(usersWithCoords);
    } catch (error) {
      console.error("Error fetching user addresses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAddresses();
  }, [accessToken]);

  return {
    userAddresses,
    loading,
    selectedDistricts,
    setSelectedDistricts,
    refresh: fetchUserAddresses,
  };
};

// User Address Controls Component
const UserAddressControls: React.FC<UserAddressControlsProps> = ({
  userAddresses = [],
  userAddressesLoading = false,
  selectedUserDistricts = [],
  userDistricts = [],
  toggleUserDistrict,
  clearUserDistrictFilters,
  refreshUserAddresses,
  showNoOrdersCallback,
}) => {
  const [showUserAddresses, setShowUserAddresses] = useState<boolean>(false);

  const [showNoOrders, setShowNoOrders] = useState(false);

  // Add this function
  const toggleNoOrders = () => {
    setShowNoOrders((prev) => !prev);
  };
  if (!userAddresses.length && !userAddressesLoading) return null;

  const addressesWithCoordinates = userAddresses.filter(
    (user: UserAddress) => user.favourite_latitude && user.favourite_longitude
  );

  const districtColors: DistrictColors = {
    Cannaregio: "#e41a1c",
    Castello: "#377eb8",
    "San Marco": "#4daf4a",
    Dorsoduro: "#984ea3",
    "Santa Croce": "#ff7f00",
    "San Polo": "#ffff33",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: "70px",
        left: "50px",
        background: "white",
        borderRadius: "8px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        minWidth: "250px",
        zIndex: 1000,
      }}
    >
      {/* Toggle Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #e5e7eb",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "#f8fafc",
          borderRadius: "8px 8px 0 0",
        }}
        onClick={() => setShowUserAddresses(!showUserAddresses)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              background: "#3b82f6",
            }}
          ></div>
          <span style={{ fontWeight: "bold", fontSize: "14px" }}>
            User Addresses
          </span>
          {userAddressesLoading && (
            <div
              style={{
                width: "12px",
                height: "12px",
                border: "2px solid #e5e7eb",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
          )}
        </div>
        <div
          style={{
            fontSize: "12px",
            color: "#6b7280",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <span>{addressesWithCoordinates.length} users</span>
          <span
            style={{
              transform: showUserAddresses ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          >
            ▼
          </span>
        </div>
      </div>

      {/* Collapsible Content */}
      {showUserAddresses && (
        <div style={{ padding: "16px" }}>
          {/* Controls */}
          <div
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "12px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={refreshUserAddresses}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                border: "1px solid #d1d5db",
                borderRadius: "4px",
                background: "white",
                cursor: "pointer",
                color: "#374151",
              }}
            >
              Refresh
            </button>
            {selectedUserDistricts.length > 0 && (
              <button
                onClick={clearUserDistrictFilters}
                style={{
                  padding: "6px 12px",
                  fontSize: "12px",
                  border: "1px solid #ef4444",
                  borderRadius: "4px",
                  background: "#fef2f2",
                  cursor: "pointer",
                  color: "#ef4444",
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* District Filters */}
          {userDistricts.length > 0 && (
            <div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  maxHeight: "150px",
                  overflowY: "auto",
                }}
              >
                {/* No Orders Filter */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    padding: "8px",
                    backgroundColor: "#f9fafb",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    marginBottom: "12px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showNoOrders}
                    onChange={() => {
                      toggleNoOrders && toggleNoOrders();
                      showNoOrdersCallback &&
                        showNoOrdersCallback(!showNoOrders);
                    }}
                    style={{ cursor: "pointer" }}
                  />
                  <div
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      backgroundColor: "#6b7280",
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: "#374151", fontWeight: "500" }}>
                    Show addresses with no orders
                  </span>
                </label>
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Filter by District:
                </h4>
                {/* Districts - merged and deduplicated */}
                {Array.from(new Set(userDistricts)).map((district: string) => {
                  const districtUsers = userAddresses.filter(
                    (user: UserAddress) =>
                      user.favourite_district === district &&
                      user.favourite_latitude &&
                      user.favourite_longitude
                  );

                  const total = districtUsers.length;
                  const withOrders = districtUsers.filter(
                    (user) => (user.n_ordini || 0) > 0
                  ).length;
                  const withoutOrders = total - withOrders;

                  return (
                    <label
                      key={district}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        cursor: "pointer",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedUserDistricts.includes(district)}
                        onChange={() =>
                          toggleUserDistrict && toggleUserDistrict(district)
                        }
                        style={{ cursor: "pointer" }}
                      />
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          backgroundColor:
                            districtColors[district] || "#888888",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ color: "#374151", flex: 1 }}>
                        {district}
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: "4px",
                          fontSize: "11px",
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: "#e5e7eb",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: "500",
                          }}
                        >
                          {total}
                        </span>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Stats */}
          <div
            style={{
              marginTop: "12px",
              padding: "8px",
              background: "#f8fafc",
              borderRadius: "4px",
              fontSize: "12px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#6b7280" }}>Total Users:</span>
              <span style={{ fontWeight: "600", color: "#374151" }}>
                {addressesWithCoordinates.length}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#6b7280" }}>Districts:</span>
              <span style={{ fontWeight: "600", color: "#374151" }}>
                {userDistricts.length}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#6b7280" }}>With Orders:</span>
              <span style={{ fontWeight: "600", color: "#16a34a" }}>
                {
                  addressesWithCoordinates.filter(
                    (u: UserAddress) => u.n_ordini > 0
                  ).length
                }
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Add CSS for spin animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// User Addresses Layer Component
const UserAddressesLayer: React.FC<UserAddressesLayerProps> = ({
  map,
  userAddresses,
  selectedDistricts,
  onAddressClick,
  showZeroOrderAdress,
}) => {
  const [userMarkersGeoJSON, setUserMarkersGeoJSON] = useState<any>(null);

  const districtColors: DistrictColors = {
    Cannaregio: "#e41a1c",
    Castello: "#377eb8",
    "San Marco": "#4daf4a",
    Dorsoduro: "#984ea3",
    "Santa Croce": "#ff7f00",
    "San Polo": "#ffff33",
  };

  useEffect(() => {
    if (!userAddresses.length) return;

    const filteredUsers = userAddresses.filter(
      (user: UserAddress) =>
        selectedDistricts.length === 0 ||
        selectedDistricts.includes(user.favourite_district)
    );

    console.log("showZeroOrderAdress", showZeroOrderAdress);
    const finalFilter = !showZeroOrderAdress
      ? filteredUsers.filter((user: UserAddress) => user.n_ordini > 0)
      : filteredUsers;
    const geoJSON = {
      type: "FeatureCollection",
      features: finalFilter.map((user: UserAddress) => ({
        type: "Feature",
        properties: {
          id: user.id,
          name: user.name,
          email: user.email,
          phone: user.phone,
          district: user.favourite_district,
          orders: user.n_ordini || 0,
          totalSpent: user.spesa_totale || 0,
          refunded: user.rimborsato || 0,
          firstOrder: user.first_order,
          lastOrder: user.last_order,
          civic_number: user.favourite_civic_number,
          color:
            districtColors[user.favourite_district as keyof DistrictColors] ||
            "#888888",
        },
        geometry: {
          type: "Point",
          coordinates: [user.favourite_longitude, user.favourite_latitude],
        },
      })),
    };

    setUserMarkersGeoJSON(geoJSON);
  }, [userAddresses, selectedDistricts, showZeroOrderAdress]);

  useEffect(() => {
    const handleUserMarkerClick = (e: any) => {
      e.originalEvent.stopPropagation();

      const features = map?.queryRenderedFeatures(e.point, {
        layers: ["user-markers"],
      });
      console.log("clicking...");

      if (features?.length) {
        const user = features[0].properties;

        if (!user) return;

        const formatDate = (dateString: string | null) => {
          if (!dateString) return "Never";
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          } catch {
            return "Invalid date";
          }
        };

        const getTimeSinceLastOrder = (dateString: string | null) => {
          if (!dateString) return "Never ordered";
          try {
            const lastOrderDate = new Date(dateString);
            const now = new Date();
            const diffTime = Math.abs(now.getTime() - lastOrderDate.getTime());
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
            const diffMinutes = Math.floor(diffTime / (1000 * 60));

            if (diffDays > 0) {
              return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
            } else if (diffHours > 0) {
              return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
            } else {
              return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
            }
          } catch {
            return "Unknown";
          }
        };

        // Helper function to calculate customer lifetime
        const getCustomerLifetime = (
          firstOrder: string | null,
          lastOrder: string | null
        ) => {
          if (!firstOrder || !lastOrder) return "N/A";
          try {
            const first = new Date(firstOrder);
            const last = new Date(lastOrder);
            const diffTime = Math.abs(last.getTime() - first.getTime());
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            const diffMonths = Math.floor(diffDays / 30);

            if (diffMonths > 0) {
              return `${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
            } else {
              return `${diffDays} day${diffDays > 1 ? "s" : ""}`;
            }
          } catch {
            return "Unknown";
          }
        };

        // Helper function to calculate average order value
        const getAverageOrderValue = (
          totalSpent: number | null,
          orderCount: number
        ) => {
          if (!totalSpent || orderCount === 0) return "N/A";
          return `€${(totalSpent / orderCount).toFixed(2)}`;
        };

        // Helper function to get customer status
        const getCustomerStatus = (
          orderCount: number,
          totalSpent: number | null,
          lastOrder: string | null
        ) => {
          if (orderCount === 0)
            return { status: "New Customer", color: "#3b82f6" };

          if (lastOrder) {
            const daysSinceLastOrder = Math.floor(
              (new Date().getTime() - new Date(lastOrder).getTime()) /
                (1000 * 60 * 60 * 24)
            );

            if (daysSinceLastOrder > 30) {
              return { status: "Inactive", color: "#ef4444" };
            }
          }

          if (orderCount >= 10)
            return { status: "VIP Customer", color: "#f59e0b" };
          if (orderCount >= 5)
            return { status: "Regular Customer", color: "#10b981" };
          return { status: "Active Customer", color: "#6366f1" };
        };

        const customerStatus = getCustomerStatus(
          user.orders || 0,
          user.totalSpent,
          user.lastOrder
        );

        // Create comprehensive popup content
        const popupHTML = `
          <div style="
            padding: 16px; 
            max-width: 350px; 
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.4;
          ">
            <!-- Header -->
            <div style="
              display: flex; 
              align-items: center; 
              gap: 12px; 
              margin-bottom: 16px; 
              padding-bottom: 12px; 
              border-bottom: 2px solid #e5e7eb;
            ">
              <div style="
                width: 48px; 
                height: 48px; 
                border-radius: 50%; 
                background: linear-gradient(135deg, ${
                  customerStatus.color
                }22, ${customerStatus.color}44); 
                display: flex; 
                align-items: center; 
                justify-content: center; 
                font-size: 20px; 
                font-weight: bold; 
                color: ${customerStatus.color};
              ">
                ${(user.name || "U").charAt(0).toUpperCase()}
              </div>
              <div style="flex: 1;">
                <h3 style="
                  margin: 0; 
                  font-size: 18px; 
                  font-weight: bold; 
                  color: #1f2937;
                ">
                  ${user.name || "Unknown User"}
                </h3>
                <div style="
                  background: ${customerStatus.color}; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 12px; 
                  font-size: 11px; 
                  font-weight: 600; 
                  display: inline-block; 
                  margin-top: 4px;
                ">
                  ${customerStatus.status}
                </div>
              </div>
            </div>

            <!-- Contact Info -->
            <div style="margin-bottom: 16px;">
              <h4 style="
                margin: 0 0 8px 0; 
                font-size: 14px; 
                font-weight: 600; 
                color: #374151;
                display: flex;
                align-items: center;
                gap: 6px;
              ">
                📞 Contact Information
              </h4>
              <div style="background: #f9fafb; padding: 10px; border-radius: 6px; font-size: 13px;">
                <div style="margin-bottom: 4px;">
                  <strong>Email:</strong> ${user.email || "Not provided"}
                </div>
                <div style="margin-bottom: 4px;">
                  <strong>Phone:</strong> ${user.phone || "Not provided"}
                </div>
                <div>
                  <strong>District:</strong> 
                  <span style="
                    background: ${
                      districtColors[user.district as keyof DistrictColors] ||
                      "#888888"
                    }; 
                    color: white; 
                    padding: 2px 6px; 
                    border-radius: 4px; 
                    font-size: 11px; 
                    margin-left: 4px;
                  ">
                    ${user.district || "Unknown"}
                  </span>
                </div>
              </div>
            </div>

            <!-- Order Statistics -->
            <div style="margin-bottom: 16px;">
              <h4 style="
                margin: 0 0 8px 0; 
                font-size: 14px; 
                font-weight: 600; 
                color: #374151;
                display: flex;
                align-items: center;
                gap: 6px;
              ">
                📊 Order Statistics
              </h4>
              <div style="
                display: grid; 
                grid-template-columns: 1fr 1fr; 
                gap: 8px; 
                font-size: 13px;
              ">
                <div style="background: #eff6ff; padding: 8px; border-radius: 6px; text-align: center;">
                  <div style="font-size: 18px; font-weight: bold; color: #1d4ed8;">
                    ${user.orders || 0}
                  </div>
                  <div style="color: #64748b; font-size: 11px;">Total Orders</div>
                </div>
                <div style="background: #f0fdf4; padding: 8px; border-radius: 6px; text-align: center;">
                  <div style="font-size: 18px; font-weight: bold; color: #16a34a;">
                    €${user.totalSpent.toFixed(2) || 0}
                  </div>
                  <div style="color: #64748b; font-size: 11px;">Total Spent</div>
                </div>
                <div style="background: #fef7cd; padding: 8px; border-radius: 6px; text-align: center;">
                  <div style="font-size: 14px; font-weight: bold; color: #a16207;">
                    ${getAverageOrderValue(user.totalSpent, user.orders)}
                  </div>
                  <div style="color: #64748b; font-size: 11px;">Avg Order</div>
                </div>
                <div style="background: #fef2f2; padding: 8px; border-radius: 6px; text-align: center;">
                  <div style="font-size: 14px; font-weight: bold; color: #dc2626;">
                    €${user.refunded.toFixed(2) || 0}
                  </div>
                  <div style="color: #64748b; font-size: 11px;">Refunded</div>
                </div>
              </div>
            </div>

            <!-- Order Timeline -->
            <div style="margin-bottom: 16px;">
              <h4 style="
                margin: 0 0 8px 0; 
                font-size: 14px; 
                font-weight: 600; 
                color: #374151;
                display: flex;
                align-items: center;
                gap: 6px;
              ">
                ⏰ Order Timeline
              </h4>
              <div style="background: #f9fafb; padding: 12px; border-radius: 6px; font-size: 13px;">
                <div style="
                  display: flex; 
                  justify-content: space-between; 
                  align-items: center; 
                  margin-bottom: 8px; 
                  padding-bottom: 8px; 
                  border-bottom: 1px solid #e5e7eb;
                ">
                  <span style="color: #6b7280;">First Order:</span>
                  <span style="font-weight: 500; color: #374151;">
                    ${formatDate(user.firstOrder)}
                  </span>
                </div>
                <div style="
                  display: flex; 
                  justify-content: space-between; 
                  align-items: center; 
                  margin-bottom: 8px; 
                  padding-bottom: 8px; 
                  border-bottom: 1px solid #e5e7eb;
                ">
                  <span style="color: #6b7280;">Last Order:</span>
                  <span style="font-weight: 500; color: #374151;">
                    ${formatDate(user.lastOrder)}
                  </span>
                </div>
           
                ${
                  user.lastOrder
                    ? `
                  <div style="
                    margin-top: 12px; 
                    padding: 8px; 
                    background: #fef3c7; 
                    border-radius: 4px; 
                    text-align: center;
                    font-size: 12px;
                    font-weight: 500;
                    color: #92400e;
                  ">
                    Last seen: ${getTimeSinceLastOrder(user.lastOrder)}
                  </div>
                `
                    : `
                  <div style="
                    margin-top: 12px; 
                    padding: 8px; 
                    background: #dbeafe; 
                    border-radius: 4px; 
                    text-align: center;
                    font-size: 12px;
                    font-weight: 500;
                    color: #1e40af;
                  ">
                    New customer - no orders yet
                  </div>
                `
                }
              </div>
            </div>

            <!-- Location Info -->
            <div style="
              background: #f3f4f6; 
              padding: 10px; 
              border-radius: 6px; 
              font-size: 12px; 
              color: #6b7280; 
              text-align: center;
            ">
              📍 Location: ${user.district},${user.civic_number}
              <br>
              User ID: #${user.id}
            </div>
          </div>
        `;
        console.log(user);
        onAddressClick(popupHTML);
        //   new (window as any).mapboxgl.Popup({
        //     maxWidth: "400px",
        //     className: "user-address-popup",
        //   })
        //     .setLngLat(e.lngLat)
        //     .setHTML(popupHTML)
        //     .addTo(map);
      }
    };

    const handleUserMarkerHover = () => {
      if (map?.getCanvas) {
        map.getCanvas().style.cursor = "pointer";
      }
    };

    const handleUserMarkerLeave = () => {
      if (map?.getCanvas) {
        map.getCanvas().style.cursor = "";
      }
    };

    map?.on("click", "user-markers", handleUserMarkerClick);
    map?.on("mouseenter", "user-markers", handleUserMarkerHover);
    map?.on("mouseleave", "user-markers", handleUserMarkerLeave);

    return () => {
      map?.off("click", "user-markers", handleUserMarkerClick);
      map?.off("mouseenter", "user-markers", handleUserMarkerHover);
      map?.off("mouseleave", "user-markers", handleUserMarkerLeave);
    };
  }, [map]);

  if (!userMarkersGeoJSON) return null;

  return (
    <Source id="user-addresses" type="geojson" data={userMarkersGeoJSON}>
      <Layer
        id="user-markers"
        type="circle"
        paint={{
          "circle-radius": [
            "case",
            [">", ["get", "orders"], 0],
            8, // Larger radius for users with orders
            6, // Smaller radius for users without orders
          ],
          "circle-color": ["get", "color"],
          "circle-stroke-width": 2,
          "circle-stroke-color": "#ffffff",
          "circle-opacity": 0.8,
        }}
      />
      <Layer
        id="user-markers-count"
        type="symbol"
        filter={[">", ["get", "orders"], 0]}
        layout={{
          "text-field": ["to-string", ["get", "orders"]],
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-size": 10,
          "text-anchor": "center",
        }}
        paint={{
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        }}
      />
    </Source>
  );
};

const mapLayer = {
  id: "route",
  type: "line" as const,
  source: "route",
  layout: {
    "line-join": "round" as const,
    "line-cap": "round" as const,
  },
  paint: {
    "line-color": "orange",
    "line-width": 5,
  },
};

const OrderIcons = (
  <FontAwesomeIcon icon={faLocationDot} bounce style={{ fontSize: 35 }} />
);

const RunnerIcons = (
  <FontAwesomeIcon icon={faPersonWalking} bounce style={{ fontSize: 25 }} />
);
const StartLocationIcon = (
  <FontAwesomeIcon
    icon={faLocationDot}
    style={{ fontSize: 50, color: "orange" }}
  />
);
const DeliveryIcon = (
  <FontAwesomeIcon
    icon={faLocationDot}
    beat
    style={{ color: "#85d016", fontSize: 50 }}
  />
);

const MapAdmin: React.FC<MapAdminProps> = (props) => {
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [Orders, setOrders] = useState<Order[]>([]);
  const [runnerOrders, setRunnerOrders] = useState<Order[]>([]);
  const dispatchRedux = useDispatch();
  const [Runners, setRunners] = useState<Runner[]>([]);
  const [tokenInvalid, setTokenIvalid] = useState<boolean>(false);
  const today = DateTime.local().toFormat("yyyy-MM-dd");
  const showRunners = useSelector((state: RootState) => state.showRunners);
  const showHelps = useSelector((state: RootState) => state.showHelps);

  const RunnersRef = useRef<Runner[]>(Runners);
  const [selectedRUnners, setSelectedRUnners] = useState<any[]>([]);

  // Add the user addresses hook
  const {
    userAddresses,
    loading: userAddressesLoading,
    selectedDistricts,
    setSelectedDistricts,
    refresh: refreshUserAddresses,
  } = useUserAddresses(props.access_token);

  // Helper functions for user addresses
  const toggleUserDistrict = (district: string): void => {
    setSelectedDistricts((prev) =>
      prev.includes(district)
        ? prev.filter((d) => d !== district)
        : [...prev, district]
    );
  };

  const clearUserDistrictFilters = (): void => {
    setSelectedDistricts([]);
  };

  const getUserDistricts = (): string[] => {
    return Array.from(
      new Set(userAddresses.map((user: UserAddress) => user.favourite_district))
    ).sort();
  };

  useEffect(() => {
    setOrders([]);
    if (!navigator.geolocation) {
      alert("Turn on Your location");
      return;
    }

    navigator?.permissions?.query({ name: "geolocation" }).then((result) => {
      if (result.state === "granted" || result.state === "prompt") {
        getLocation();
      }
    });

    function getLocation(): void {
      console.log("ciao");
    }
  }, []);

  useEffect(() => {
    RunnersRef.current = Runners;
  }, [Runners]);

  useEffect(() => {
    setTokenIvalid(false);
    getRunners({
      accessToken: props.access_token,
    });
    getOrders({
      date_from: today,
      date_to: today,
      accessToken: props.access_token,
    });
  }, [props.refresh, showRunners.show_runners, props.access_token]);

  async function getRunners({
    accessToken,
  }: {
    accessToken: string;
  }): Promise<void> {
    if (accessToken && !tokenInvalid) {
      const urlParams = new URLSearchParams({
        created_at: DateTime.local().toFormat("yyyy-MM-dd"),
      });
      const url = `${
        process.env.REACT_APP_API
      }/runner-position/?${urlParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        console.log("Network response was not ok " + response.statusText);
        if (response.status == 401) {
          setTokenIvalid(true);
        }
      } else {
        const responseData = await response.json();

        const uniqueData = removeDuplicatesById(responseData.data);

        setRunners(uniqueData);
      }
    }
  }

  const removeDuplicatesById = (array: Runner[]): Runner[] => {
    const seenIds = new Set<number>();
    return array.filter((item: Runner) => {
      if (seenIds.has(item.runner_id)) {
        return false;
      }
      seenIds.add(item.runner_id);
      return true;
    });
  };

  async function getOrders({
    date_from,
    date_to,
    accessToken,
  }: GetOrdersParams): Promise<void> {
    if (accessToken && !tokenInvalid) {
      const urlParams = new URLSearchParams({ date_from, date_to });
      const url = `${
        process.env.REACT_APP_API
      }/food/order/?${urlParams.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${props.access_token}`,
        },
      });

      if (!response.ok) {
        console.log("Network response was not ok " + response.statusText);
        if (response.status == 401) {
          setTokenIvalid(true);
        }
      } else {
        const responseData = await response.json();
        setRunnerOrders(responseData.data);
      }
    }
  }

  const isRunnerActive = (runner: Runner): boolean => {
    const start = new Date();
    start.setMinutes(start.getMinutes() - 30);
    const end = new Date();
    end.setMinutes(end.getMinutes() + 30);

    const realTime = `${new Date().getHours()}:${new Date().getMinutes()}:${new Date().getSeconds()}`;
    const startTime = `${start.getHours()}:${start.getMinutes()}:${start.getSeconds()}`;
    const endTime = `${end.getHours()}:${end.getMinutes()}:${end.getSeconds()}`;

    const active = runner?.users?.runner_days?.find((rd: any) => {
      const start_range =
        rd?.saturation_days?.range_hour?.start_range?.split(/T|\./)[1];
      const end_range =
        rd?.saturation_days?.range_hour?.end_range?.split(/T|\./)[1];

      return (
        (start_range <= startTime || start_range <= realTime) &&
        (end_range >= endTime || end_range >= realTime)
      );
    });
    return !!active || !!runner?.users?.runner_orders?.length;
  };

  useEffect(() => {
    if (props.showDirectioAdmin) {
      const order = props.showDirectioAdmin;
      let path_to_send = {
        lat: JSON.stringify(order.store.latitude),
        lng: JSON.stringify(order.store.longitude),
        lat1: JSON.stringify(order.food_orders)
          ? JSON.stringify(order.food_orders.latitude)
          : JSON.stringify(order.smart_logistics_orders)
          ? JSON.stringify(order.food_orders.smart_logistics_orders.latitude)
          : null,
        lng1: JSON.stringify(order.food_orders)
          ? JSON.stringify(order.food_orders.longitude)
          : JSON.stringify(order.smart_logistics_orders)
          ? JSON.stringify(order.food_orders.smart_logistics_orders.longitude)
          : null,
        steps: {
          duration: Number(JSON.stringify(order.runner_walking_time)),
          distance: JSON.stringify(order.distance),
        },
      };
      props.showpath(path_to_send);

      props.onReceiveData({
        steps: {
          duration: path_to_send.steps.duration,
          distance: path_to_send.steps.distance,
        },
      });
    }
  }, [props.showDirectioAdmin]);

  useEffect(() => {
    // Connect to Socket.io server
    const socket = io(`${process.env.REACT_APP_URL}`, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      secure: true,
    });
    if (props.access_token) {
      // Listen for runner position updates
      socket.on("runnerposition-update", async (updatedPosition: Runner[]) => {
        if (updatedPosition && updatedPosition[0]) {
          const data: Runner[] = [...RunnersRef.current];
          const foundIndex = data.findIndex(
            (r: Runner) => r.runner_id == updatedPosition[0].runner_id
          );
          if (foundIndex != -1) {
            data[foundIndex] = updatedPosition[0];
          } else {
            data.push(updatedPosition[0]);
          }
          setRunners([...data]);
        } else {
          await getRunners({
            accessToken: props.access_token,
          });
        }
        console.log("Runner position updated:", updatedPosition);
      });
    }

    return () => {
      socket.disconnect();
    };
  }, [props.access_token]);

  useEffect(() => {
    const runners_working = Runners.filter(isRunnerActive);

    dispatchRedux(setAvailableRunners(Runners));
  }, [Runners]);

  const handleSelectedRunner = (run: Runner): void => {
    const runnnerObj = {
      type: "runner",
      data: run,
      runner_order: runnerOrders,
      accessToken: props.access_token,
    };

    dispatchRedux(setselectedRunnersView(runnnerObj));
  };

  const [routeMetrics, setRouteMetrics] = useState<{ [key: string]: any }>({});

  const handleRouteMeta = (meta: {
    distance: number;
    duration: number;
    runnerName: string;
    orderNumber: string;
  }): void => {
    setRouteMetrics((prev) => ({
      ...prev,
      [meta.orderNumber]: meta,
    }));
    console.log(
      `${meta.runnerName} - Order ${meta.orderNumber}: ${
        meta.distance
      }m, ${Math.round(meta.duration / 60)} min`
    );
  };

  function convertOrderToRouteProps(order: Order): any {
    return {
      lat: JSON.stringify(order.store.latitude),
      lng: JSON.stringify(order.store.longitude),
      lat1: order.food_orders
        ? JSON.stringify(order.food_orders.latitude)
        : order.smart_logistics_orders
        ? JSON.stringify(order.smart_logistics_orders.latitude)
        : "",
      lng1: order.food_orders
        ? JSON.stringify(order.food_orders.longitude)
        : order.smart_logistics_orders
        ? JSON.stringify(order.smart_logistics_orders.longitude)
        : "",
      steps: {
        duration: Number(JSON.stringify(order.runner_walking_time || 0)),
        distance: JSON.stringify(order.distance || 0),
      },
    };
  }

  const runnerColors: string[] = [
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FF00FF",
    "#00FFFF",
    "#FFFF00",
    "#FF8000",
    "#8000FF",
    "#FF0080",
    "#80FF00",
    "#0080FF",
    "#FF8080",
  ];

  // Get runner ID from order
  function getRunnerId(order: Order): number | null {
    const runnerOrder =
      Array.isArray(order?.runner_orders) && order.runner_orders.length
        ? [...order.runner_orders].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          )[0]
        : null;
    return runnerOrder?.user_id ?? null;
  }

  // Hash function to convert runner ID to consistent color index
  function hashRunnerId(runnerId: number | null): number {
    if (runnerId === null) return 0;
    let hash = 0;
    const str = runnerId.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash) % runnerColors.length;
  }

  // Get consistent color for runner
  function getRunnerColor(order: Order): string {
    const runnerId = getRunnerId(order);
    const colorIndex = hashRunnerId(runnerId);
    return runnerColors[colorIndex];
  }

  function hasCoords(order: Order): boolean {
    const sLat = Number(order?.store?.latitude);
    const sLng = Number(order?.store?.longitude);
    const dLat = Number(
      order?.food_orders?.latitude ?? order?.smart_logistics_orders?.latitude
    );
    const dLng = Number(
      order?.food_orders?.longitude ?? order?.smart_logistics_orders?.longitude
    );
    return [sLat, sLng, dLat, dLng].every(Number.isFinite);
  }

  const filteredOrders = React.useMemo(() => {
    return runnerOrders
      .filter(
        (o: Order) =>
          o.status.name !== OrderStatusEnum.CONSEGNATO &&
          o.status.name !== OrderStatusEnum.RIFIUTATO_DAL_RISTORANTE &&
          o.status.name !== OrderStatusEnum.RIFIUTATO_DA_COCAI
      )
      .filter(hasCoords);
  }, [runnerOrders]);

  const runnerColorMatch = React.useMemo((): RunnerColorMatch[] => {
    const byRunner = new Map<number | null, RunnerColorMatch>();

    for (const order of filteredOrders) {
      const rid = getRunnerId(order);
      const color = getRunnerColor(order);

      // find name from Runners array; fallback if missing
      const name =
        rid == null
          ? "Unassigned"
          : Runners.find((r: Runner) => r.runner_id === rid)?.users?.fullname ??
            `Runner ${rid}`;

      const prev = byRunner.get(rid);
      if (prev) {
        prev.count += 1;
      } else {
        byRunner.set(rid, { runnerId: rid, color, name, count: 1 });
      }
    }

    // Sort: assigned first, then by name
    return Array.from(byRunner.values()).sort((a, b) => {
      if (a.runnerId === null && b.runnerId !== null) return 1;
      if (a.runnerId !== null && b.runnerId === null) return -1;
      return a.name.localeCompare(b.name);
    });
  }, [filteredOrders, Runners]);

  const isAnonymousRunner = (val: unknown): boolean => {
    const s = String(val ?? "").trim();
    if (!s) return false;

    // pure digits like "54772"
    if (/^\d+$/.test(s)) return true;

    // "Runner 54772", "runner 123", etc.
    if (/^runner\s+\d+\b/i.test(s)) return true;

    return false;
  };

  const [userInfo, setUserInfo] = useState<any>();
  const [showZeroOrderAdress, setShowZeroOrderAdress] =
    useState<boolean>(false);
  const showUserAddresses = useSelector(
    (state: RootState) => state.showUserAddresses
  );
  return (
    <div>
      {/* Add User Address Controls */}
      {showUserAddresses.show_addresses && (
        <UserAddressControls
          userAddresses={userAddresses}
          userAddressesLoading={userAddressesLoading}
          selectedUserDistricts={selectedDistricts}
          userDistricts={getUserDistricts()}
          toggleUserDistrict={toggleUserDistrict}
          clearUserDistrictFilters={clearUserDistrictFilters}
          refreshUserAddresses={refreshUserAddresses}
          showNoOrdersCallback={setShowZeroOrderAdress}
        />
      )}
      {/* Add User Addresses Layer */}
      {showUserAddresses.show_addresses && (
        <UserAddressesLayer
          map={props.map}
          userAddresses={userAddresses}
          selectedDistricts={selectedDistricts}
          onAddressClick={(e: any) => setUserInfo(e)}
          showZeroOrderAdress={showZeroOrderAdress}
        />
      )}
      {userInfo && (
        <div
          style={{
            position: "absolute",
            top: 50,
            right: 10,
            background: "white",
            padding: 15,
            borderRadius: 8,
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            maxHeight: 400,
            overflowY: "auto",
            minWidth: 280,
            zIndex: 1,
          }}
        >
          {/* Close Button */}
          <button
            onClick={() => setUserInfo(null)} // or whatever your close function is
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#6b7280",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              transition: "all 0.2s ease",
              zIndex: 10,
            }}
            onMouseEnter={(e) => {
              (e.target as HTMLElement).style.background = "#f3f4f6";
              (e.target as HTMLElement).style.color = "#374151";
            }}
            onMouseLeave={(e) => {
              (e.target as HTMLElement).style.background = "none";
              (e.target as HTMLElement).style.color = "#6b7280";
            }}
          >
            ×
          </button>

          {/* Your HTML content */}
          <div dangerouslySetInnerHTML={{ __html: userInfo }} />
        </div>
      )}
      <div className="fixed  top-[20px] left-[300px] max-w-[100%] overflow-x-auto">
        {showRunners.show_runners && (
          <div className="flex flex items-center justify-center gap-[20px]">
            {Runners.filter(isRunnerActive).map(
              (runner: Runner, index: number) => {
                const activeOrders = runnerOrders.filter((order: Order) => {
                  return (
                    order.status.name !== OrderStatusEnum.CONSEGNATO &&
                    order.status.name !==
                      OrderStatusEnum.RIFIUTATO_DAL_RISTORANTE &&
                    order.status.name !== OrderStatusEnum.RIFIUTATO_DA_COCAI
                  );
                });

                const runnerOrdersFiltered = activeOrders.filter(
                  (order: Order) => {
                    return (
                      order?.runner_orders[0]?.user_id === runner?.runner_id
                    );
                  }
                );

                const urgentStatuses = [
                  OrderStatusEnum.RITIRATO,
                  OrderStatusEnum.PRONTO,
                  OrderStatusEnum.IN_ATTESA_DEL_RISTORANTE,
                ];

                const urgentOrders = runnerOrdersFiltered.filter(
                  (orddd: Order) =>
                    urgentStatuses.includes(orddd.status.name as any)
                );

                // 1. If there are urgent orders, select the one with the closest pickup time.
                // 2. Otherwise, if there are any orders, select the one with the closest pickup time.
                let orderForBorder: Order | undefined;
                if (urgentOrders.length > 0) {
                  orderForBorder = urgentOrders.sort(
                    (a: Order, b: Order) =>
                      new Date(a.pickupTime).getTime() -
                      new Date(b.pickupTime).getTime()
                  )[0];
                } else if (runnerOrdersFiltered.length > 0) {
                  orderForBorder = runnerOrdersFiltered.sort(
                    (a: Order, b: Order) =>
                      new Date(a.pickupTime).getTime() -
                      new Date(b.pickupTime).getTime()
                  )[0];
                }

                const borderStyle = orderForBorder
                  ? `${orderForBorder.status.styles.background} 5px solid`
                  : "";

                return (
                  <div
                    style={{ border: borderStyle }}
                    onClick={() => handleSelectedRunner(runner)}
                    className={`
        ${
          showRunners.selected_runners_view.some(
            (r: any) => r.data.runner_id === runner.runner_id
          )
            ? "bg-[#fab626]"
            : "bg-[grey]"
        }
        rounded-xl pl-2 pr-2 cursor-pointer
      `}
                    key={index}
                  >
                    {runner.users.fullname}
                  </div>
                );
              }
            )}
          </div>
        )}
      </div>
      {showRunners.show_runners &&
        filteredOrders.map((order: Order, index: number) => (
          <OrderRoute
            key={order.id}
            order={order}
            color={getRunnerColor(order)}
          />
        ))}
      {showRunners.show_runners && (
        <div
          style={{
            position: "absolute",
            top: 50,
            right: 10,
            background: "white",
            padding: 15,
            borderRadius: 8,
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            maxHeight: 400,
            overflowY: "auto",
            minWidth: 280,
            zIndex: 1,
          }}
        >
          <h4 style={{ margin: 0, marginBottom: 10 }}>Runner Colors</h4>
          {runnerColorMatch.length === 0 && (
            <div style={{ color: "#777" }}>No routes drawn.</div>
          )}
          {runnerColorMatch.map(
            ({ runnerId, color, name, count }: RunnerColorMatch) => (
              <div
                key={runnerId ?? "unassigned"}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid #eee",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: color,
                      border: "1px solid #ccc",
                    }}
                  />
                  <span>
                    {name}
                    {isAnonymousRunner(name) &&
                      " (no runner name because location turned off)"}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: "#666" }}>
                  {count} order{count > 1 ? "s" : ""}
                </span>
              </div>
            )
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#fff000",
                border: "1px solid #ccc",
              }}
            />
            <span>pick up</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                background: "#00ff00",
                border: "1px solid #ccc",
              }}
            />
            <span>delivery</span>
          </div>
        </div>
      )}
      {showHelps.show_helps && (
        <div className="fixed  top-[20px] right-[20px] h-[200px] w-[200px] bg-[white] rounded">
          <div className=" max-w-[100%] overflow-auto max-h-[200px]">
            {props.allOrders && (
              <div className="flex flex-col items-center justify-center gap-[5px] overflow-auto">
                {props.allOrders.map((ords: Order, index: number) => {
                  const helpReq = ords.help_requests;

                  helpReq.sort((a: any, b: any) => {
                    if (a.served === b.served) {
                      return 0;
                    }

                    return a.served ? 1 : -1;
                  });

                  return helpReq.map((help: any, helpIndex: number) => {
                    return (
                      <div
                        className={` rounded-xl pl-2 pr-2 flex flex-col gap-[10px] mt-2 cursor-pointer`}
                        key={`${index}-${helpIndex}`}
                      >
                        <div
                          className={`flex flex-col  ${
                            !help.served ? "bg-[wheat]" : "bg-[green]"
                          } p-1  rounded w-[100%]`}
                        >
                          <span>
                            Motivation: {help.motivation.replaceAll("_", " ")}
                          </span>
                          <span>Method: {help.method}</span>
                          <span>Note: {help.note}</span>
                          <span>Phone number: {help.phone_number}</span>
                          <span>
                            Served: {help.served ? "Served" : "Not Served"}
                          </span>
                        </div>
                      </div>
                    );
                  });
                })}
              </div>
            )}
          </div>
        </div>
      )}
      {showRunners.show_runners &&
        Runners.filter(isRunnerActive).map((runner: Runner, index: number) => {
          const activeRuns = runner;

          const breakCrumbs = JSON.parse(activeRuns.positions).positions.sort(
            (a: any, b: any) => {
              return (
                new Date(b?.timestamp).getTime() -
                new Date(a?.timestamp).getTime()
              );
            }
          );

          const isSelected = showRunners.selected_runners_view.some(
            (r: any) => r.data?.runner_id === activeRuns?.runner_id
          );

          return breakCrumbs.slice(0, 7).map((run: any, runIndex: number) => {
            return (
              <Marker
                key={`marker-${index}-${runIndex}`}
                longitude={
                  runIndex === 0 ? activeRuns.longitude : run.longitude
                }
                latitude={runIndex === 0 ? activeRuns.latitude : run.latitude}
                anchor="bottom"
                onClick={(e: any) => {
                  e.originalEvent.stopPropagation();
                  dispatchRedux(
                    setselectedRunners({
                      type: "runner",
                      data: activeRuns,
                      runner_order: runnerOrders,
                      accessToken: props.access_token,
                    })
                  );
                  props.onrecieveRunnerDetail({
                    type: "runner",
                    data: run,
                    runner_order: runnerOrders,
                    accessToken: props.access_token,
                  });
                }}
              >
                {runIndex === 0 ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignContent: "center",
                      justifyItems: "center",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        background: isSelected ? "orange" : "grey",
                        borderRadius: "10px",
                        padding: "0 5px 0 5px ",
                      }}
                    >
                      <h2>{runner.users.fullname}</h2>
                    </div>
                    <img
                      style={{
                        height: "40px",
                        width: "40px",
                        borderRadius: "50%",
                        border: isSelected
                          ? "2px solid orange"
                          : "2px solid grey",
                      }}
                      src={`${process.env.REACT_APP_API}/media/upload/runnerProfile/${runner.users.id}`}
                      onError={(
                        e: React.SyntheticEvent<HTMLImageElement, Event>
                      ) => {
                        const target = e.target as HTMLImageElement;
                        target.onerror = null;
                        target.src = `${process.env.PUBLIC_URL}/user.png`;
                      }}
                      alt="Profile"
                    />
                  </div>
                ) : (
                  <CircleIcon style={{ color: "green", fontSize: "5px" }} />
                )}
              </Marker>
            );
          });
        })}
      {props.steps &&
        props.steps?.legs[0]?.steps &&
        props.steps?.legs[0]?.steps?.map((step: any, index: number) => (
          <Marker
            key={`step-${index}`}
            longitude={step.maneuver.location[0]}
            latitude={step.maneuver.location[1]}
            anchor="bottom"
            onClick={(e: any) => {
              e.originalEvent.stopPropagation();
            }}
          >
            {index === 0 ? (
              StartLocationIcon
            ) : index === props.steps.legs[0].steps.length - 1 ? (
              DeliveryIcon
            ) : (
              <i
                className="fa fa-map-marker"
                title="Step"
                style={{ color: "red", fontSize: "1em", display: "none" }}
              ></i>
            )}
          </Marker>
        ))}
      {props.route && (
        <Source type="geojson" data={props.route}>
          <Layer {...mapLayer} />
        </Source>
      )}
      {props.popupInfo && (
        <Popup
          anchor="top"
          longitude={Number(props.popupInfo.marker.longitude)}
          latitude={Number(props.popupInfo.marker.latitude)}
          onClose={() => {
            props.removePopUp();
            setPopupInfo(null);
          }}
          className="custom-popup"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <div>
              <span className="popup_address_text">
                {props.popupInfo.marker.business_name}
              </span>
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default MapAdmin;
